import { TranslationFunction } from 'i18next';
import * as React from 'react';
import { translate } from 'react-i18next';
import { RouteComponentProps, withRouter } from 'react-router';
import * as url from 'url';
import AppInsightsService from '../app-insights/service';
import { HOST, LANGUAGE_CODE_EN_CA, LANGUAGE_CODE_FR_CA, ROUTE_PATH_HOME, SHOW_MAINTENANCE_PAGE } from '../constants';
import { default as i18n, getDocumentTitle } from '../locales';
import {  MIXPANEL_PROPERTY_PAGE_TITLE } from '../mixpanel.service';
import * as AppUtils from '../utils';
import { NavMenu } from './nav-menu';
import { RouterNavLink, RouterNavLinkProps } from './router-nav-link';
import logo from '../assets/img/bmo-onguard-ida-logo-EN.svg';
import maintenanceLogo from '../assets/img/logo-header-en-ca.svg';
import logoFr from '../assets/img/bmo-onguard-ida-logo-FR.svg';
import maintenanceLogoFr from '../assets/img/logo-header-fr-ca.svg';

interface SiteHeaderComponentProps extends RouteComponentProps<{}> {
  t: TranslationFunction;
  onClick?: {
    [key: string]: (event: React.MouseEvent) => void;
  };
  children?: void;
}

class SiteHeaderComponent extends React.Component<SiteHeaderComponentProps> {
  constructor(props: SiteHeaderComponentProps) {
    super(props);

    this.onLogoImageLinkClick = this.onLogoImageLinkClick.bind(this);
    this.onSkipLinkClick = this.onSkipLinkClick.bind(this);
    this.onNavLinkClick = this.onNavLinkClick.bind(this);
  }

  public shouldComponentUpdate(nextProps: Readonly<SiteHeaderComponentProps>): boolean {
    return this.props.location !== nextProps.location;
  }

  public render(): React.ReactNode {
    return (
      <header className="site-header">
        <div className="row">
          <div className="col-logo-image">
            {this.renderLogo()}
            {/* <a className="sr-only" href="#mainContent" onClick={this.onSkipLinkClick}>{i18n.t('siteHeader.skipToMainContent')}</a>
            <a className="sr-only" href="#siteNavbar" onClick={this.onSkipLinkClick}>{i18n.t('siteHeader.skipToNavMenu')}</a> */}
          </div>
          <div className="col-nav-menu">{this.renderNavMenu()}</div>
        </div>
      </header>
    );
  }

  public onSkipLinkClick(event: React.MouseEvent<HTMLAnchorElement>): void {
    event.preventDefault();
    event.stopPropagation();

    const elSkipToSection = document.querySelector<HTMLElement>(event.currentTarget.getAttribute('href') as string);
    if (elSkipToSection) {
      elSkipToSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      elSkipToSection.focus();
    }
  }

  public onLogoImageLinkClick(): void {
    this.trackEvent('Click Header Logo');
  }

  public onNavLinkClick(event: React.MouseEvent<HTMLAnchorElement>): void {
    const elCurrentTarget = event.currentTarget;
    if (elCurrentTarget == null) {
      return;
    }

    const { onClick: onClickProp } = this.props;
    const key = elCurrentTarget.getAttribute('data-key') as string;
    const clickHandler = onClickProp && Object.prototype.hasOwnProperty.call(onClickProp, key) && onClickProp[key];
    const callClickHandler = () => AppUtils.isFunction(clickHandler) && clickHandler(event);

    const eventName = `Click ${AppUtils.jsPropertyNameToTitleCase(key)} Menu Item`;
    this.trackEvent(eventName);
    callClickHandler();
  }

  private renderLogo(): React.ReactNode {
    const { alt, ...logoImageProps } = this.props.t('siteHeader.logoImage') as React.ImgHTMLAttributes<HTMLImageElement>;
    logoImageProps.className = [logoImageProps.className, 'img-logo-header'].join(' ');
    // logoImageProps.tabIndex = 0;
    
    const [trimmedAlt, src] = 
    i18n.language === LANGUAGE_CODE_EN_CA
    ? [SHOW_MAINTENANCE_PAGE() ? 'OnGuard Logo' : 'OnGuard Powered by ID Assist logo', SHOW_MAINTENANCE_PAGE() ? maintenanceLogo : logo]
    : [SHOW_MAINTENANCE_PAGE() ? 'Logo du EnGarde' : 'Logo du EnGarde Fourni par assist identité', SHOW_MAINTENANCE_PAGE() ? maintenanceLogoFr : logoFr];
    
    logoImageProps.src = src;
    
    logoImageProps.height = SHOW_MAINTENANCE_PAGE() ? 24:48;

    logoImageProps.width = SHOW_MAINTENANCE_PAGE() ? 158 : 269;
    
    if (trimmedAlt.length === 0) {
      logoImageProps.role = 'presentation';
    }

    // tslint:disable:jsx-a11y/alt-text
    return (
      <RouterNavLink className="link-logo-image" to={ROUTE_PATH_HOME} onClick={this.onLogoImageLinkClick}>
        <img alt={trimmedAlt} {...logoImageProps} />
      </RouterNavLink>
    );
    // tslint:enable:jsx-a11y/alt-text
  }

  private renderNavMenu(): React.ReactNode {
    const navMenu = this.props.t('siteHeader.navMenu') as { [key: string]: RouterNavLinkProps };
    Object.keys(navMenu).forEach((key) => {
      const navLinkProps = navMenu[key];
      navLinkProps['data-key'] = key;
      navLinkProps.onClick = this.onNavLinkClick;

      if (key === 'languageToggle') {
        const altLanguageCode = i18n.language === LANGUAGE_CODE_EN_CA ? LANGUAGE_CODE_FR_CA : LANGUAGE_CODE_EN_CA;
        const altLanguageUrl = url.parse(String(window.location), true);
        altLanguageUrl.host = (HOST.find((host) => host.value === altLanguageCode) || { key: window.location.host }).key;
        navLinkProps.to = '';
        navLinkProps.href = url.format(altLanguageUrl);
      }
    });
    if(SHOW_MAINTENANCE_PAGE()) {
      delete navMenu['contact'];
    }

    return <NavMenu menu={navMenu} />;
  }

  // tslint:disable-next-line:no-any
  private trackEvent(eventName: string, props?: { [key: string]: any }) {
    const pageTitle = getDocumentTitle(window.location.pathname, { lng: LANGUAGE_CODE_EN_CA });
    const properties = Object.assign({}, { [MIXPANEL_PROPERTY_PAGE_TITLE]: pageTitle }, props);
    AppInsightsService.trackEvent({ name: eventName, properties });
    //return MixpanelService.track(eventName, properties);
  }
}

const SiteHeader = translate('global', { i18n })(SiteHeaderComponent);
export default withRouter(SiteHeader);
